import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

interface StoreFaqSectionProps {
  storeName: string;
  storeAddress: string;
  storePhone: string;
}

const faqs = (storeName: string, storeAddress: string, storePhone: string) => [
  {
    id: 'address',
    question: `What is the address of the BlueStone store in ${storeName}?`,
    answer: (
      <>
        The BlueStone store is located at{' '}
        <strong>{storeAddress}</strong>.
      </>
    ),
  },
  {
    id: 'jewellery-types',
    question: `What types of jewellery can I find at the BlueStone ${storeName} store?`,
    answer: (
      <>
        BlueStone <strong>{storeName}</strong> store offers a wide range of gold
        &amp; diamond jewellery, including engagement rings, necklaces, earrings,
        bracelets, and more. We feature both traditional and contemporary designs
        to cater to diverse tastes.
      </>
    ),
  },
  {
    id: 'hours',
    question: `What are the BlueStone ${storeName}, store operating hours?`,
    answer: (
      <>
        BlueStone <strong>{storeName}</strong> store generally opens from 11 AM
        to 9:30 PM. For any changes in hours or special holiday timings, please
        check our website or contact the store.
      </>
    ),
  },
  {
    id: 'contact',
    question: `How can I contact the BlueStone ${storeName} store?`,
    answer: <>You can contact the store by calling {storePhone}.</>,
  },
  {
    id: 'customization',
    question: `Does the store offer any customization services?`,
    answer: (
      <>
        Yes, BlueStone <strong>{storeName}</strong> provides customized
        jewellery services. Our experts can help you create unique pieces
        tailored to your preferences.
      </>
    ),
  },
  {
    id: 'repair',
    question: `Can I get jewelry repair services at BlueStone ${storeName}?`,
    answer: (
      <>
        Yes, BlueStone {storeName} provides jewellery repair services. Our
        experienced staff can handle various types of repairs, including
        resizing, cleaning, and fixing damaged pieces. For a detailed assessment
        and to discuss your repair needs, please visit the store or contact us
        directly.
      </>
    ),
  },
  {
    id: 'promotions',
    question: `Are there any special promotions or discounts available in BlueStone ${storeName}?`,
    answer: (
      <>
        We often have special promotions and discounts. For the latest offers,
        please visit our website or inquire directly at the {storeName} store.
      </>
    ),
  },
  {
    id: 'return',
    question: `Is there a return or exchange policy for purchases made at this store?`,
    answer: (
      <>
        Yes, BlueStone has a return and exchange policy. Please review our
        policy details{' '}
        <a
          href="/shipping-return.html#lifetime-exchange"
          className="underline text-[#0d2750]"
        >
          here
        </a>{' '}
        or speak with the BlueStone {storeName} store representative for more
        information.
      </>
    ),
  },
  {
    id: 'appointment',
    question: `Can I make an appointment to view specific jewellery pieces?`,
    answer: (
      <>
        Yes, if you are interested in viewing specific items or need a private
        shopping experience, you can directly visit the store near you. Or you
        can try our jewellery at the comfort of your home with our{' '}
        <a href="/try-at-home.html" className="underline text-[#0d2750]">
          try at home service
        </a>
        .
      </>
    ),
  },
  {
    id: 'payment',
    question: `What modes of payment are acceptable at BlueStone Jewellery ${storeName} Store?`,
    answer: (
      <>
        BlueStone Jewellery in {storeName} accepts cash, debit/credit cards, net
        banking, mobile wallets, and UPI.
      </>
    ),
  },
];

export function StoreFaqWidget({
  storeName,
  storeAddress,
  storePhone,
}: StoreFaqSectionProps) {
  if (!storeName) return null;

  const items = faqs(storeName, storeAddress, storePhone);

  return (
    <div className="mx-4 my-6 md:mx-8">
      <h2 className="mb-4 text-xl font-bold text-[#0d2750]">
        Frequently Asked Questions
      </h2>
      <AccordionPrimitive.Root type="multiple" className="flex flex-col gap-3">
        {items.map((item) => (
          <AccordionPrimitive.Item
            key={item.id}
            value={item.id}
            className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
          >
            <AccordionPrimitive.Header className="flex">
              <AccordionPrimitive.Trigger
                className={cn(
                  'group flex w-full items-start gap-3 px-4 py-4 text-left text-sm font-semibold text-[#0d2750]',
                  'transition-colors hover:bg-gray-50'
                )}
              >
                <span className="mt-0.5 shrink-0">
                  <ChevronUp className="h-4 w-4 group-data-[state=open]:block hidden" />
                  <ChevronDown className="h-4 w-4 group-data-[state=open]:hidden block" />
                </span>
                <span className="flex-1">{item.question}</span>
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionPrimitive.Content className="overflow-hidden text-sm text-gray-700 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
              <div className="px-4 pb-4 pt-0 pl-11">{item.answer}</div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        ))}
      </AccordionPrimitive.Root>
    </div>
  );
}
